import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { fetchData, fetchJson } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import {
  Button,
  Card,
  Empty,
  ErrorBanner,
  FormattedDate,
  Loading,
  PageHeader,
  Tag,
} from '../../components/ui'
import { flagIfAuthError } from '../../components/ResetBanner'

type DailyReport = {
  id: string
  project_id: string
  report_date?: number
  status?: string
  weather?: string | null
  manpower_total?: number | null
  notes?: string | null
}

type DailyReportListResponse = {
  reports: DailyReport[]
  total: number
}

export function DailyReportListPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const qc = useQueryClient()
  const q = useQuery<DailyReportListResponse>({
    queryKey: ['daily-reports', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      fetchData<DailyReportListResponse>(
        orgPath(`/daily-reports?project_id=${projectId}`),
      ),
  })

  const publish = useMutation({
    mutationFn: (reportId: string) =>
      fetchJson(orgPath(`/daily-reports/${reportId}/publish`), { method: 'POST' }),
    onError: flagIfAuthError,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['daily-reports', projectId] }),
  })

  if (q.isLoading) return <Loading />
  if (q.isError) return <ErrorBanner error={q.error} />

  const reports = q.data?.reports ?? []

  return (
    <div>
      <PageHeader
        title="Daily reports"
        actions={
          <Link
            to="new"
            className="rounded-md bg-accent-500 hover:bg-accent-600 px-3 py-1.5 text-sm font-medium text-white"
          >
            New report
          </Link>
        }
      />
      {reports.length === 0 ? (
        <Empty
          message="No daily reports filed yet."
          hint="File one to capture today's manpower, materials used, and notes."
        />
      ) : (
        <Card>
          {publish.isError && <ErrorBanner error={publish.error} />}
          <ul className="divide-y divide-surface-700 text-sm">
            {reports.map((r) => (
              <li key={r.id} className="py-3 flex justify-between items-start gap-3">
                <div>
                  <div className="font-medium">
                    <FormattedDate ts={r.report_date ?? null} />
                  </div>
                  {r.notes && (
                    <div className="text-xs text-surface-400 mt-1 line-clamp-2">{r.notes}</div>
                  )}
                </div>
                <div className="text-right space-y-1">
                  <Tag tone={r.status === 'published' ? 'success' : 'warn'}>
                    {r.status ?? '—'}
                  </Tag>
                  {r.manpower_total !== null && r.manpower_total !== undefined && (
                    <div className="text-xs text-surface-400">
                      {r.manpower_total} manpower
                    </div>
                  )}
                  {r.status !== 'published' && (
                    <div className="flex justify-end">
                      <Button
                        onClick={() => publish.mutate(r.id)}
                        disabled={publish.isPending && publish.variables === r.id}
                      >
                        {publish.isPending && publish.variables === r.id
                          ? 'Publishing…'
                          : 'Publish'}
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}
